'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @a11y-contract NumericPagination (client navigation)
 *
 * Role:           <nav aria-label="Pagination"> wrapping a list of page buttons.
 *                 Current page: aria-current="page" + aria-disabled="true" (still
 *                 visible and readable, just non-interactive).
 *                 Ellipsis: <span aria-hidden="true"> — AT doesn't need to read "..."
 *                 since the page list itself communicates range.
 * RSC:            No — 'use client' (active state, keyboard handler, live announce).
 * Focus:          Each page button is focusable. Current page button is focusable
 *                 but disabled (aria-disabled) so AT reads it without activating.
 * Keyboard:       Home → first page, End → last page, Arrow left/right to move between
 *                 page buttons. Enter/Space activates the focused page.
 * Forced-colors:  Active page: Highlight/HighlightText colours. Buttons draw ButtonText.
 * RTL:            Prev/next chevrons use data-icon-mirror; flex order is natural (LTR).
 * Reduced-motion: No animation.
 * Touch target:   Each page button is min 36×36 (dense layout acceptable in paginator).
 * Labels:         Page buttons: aria-label="Page {N}"; current: aria-label="Page {N}, current".
 * Live region:    On page change, a visually-hidden aria-live="polite" announces
 *                 "Page {N} of {total}" to AT.
 * PRM (Pillar 12):gap/padding use --space-* tokens.
 */
import { useRef, useState, useEffect, } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './NumericPagination.module.css';
/**
 * Computes visible page numbers with ellipsis. Returns an array of
 * numbers and -1 for ellipsis positions.
 */
function buildPages(current, total, max) {
    if (total <= max)
        return Array.from({ length: total }, (_, i) => i + 1);
    const half = Math.floor(max / 2);
    let start = Math.max(current - half, 1);
    let end = start + max - 1;
    if (end > total) {
        end = total;
        start = Math.max(end - max + 1, 1);
    }
    const pages = [];
    if (start > 1) {
        pages.push(1);
        if (start > 2)
            pages.push(-1); // ellipsis
    }
    for (let p = start; p <= end; p++)
        pages.push(p);
    if (end < total) {
        if (end < total - 1)
            pages.push(-1); // ellipsis
        pages.push(total);
    }
    return pages;
}
export function NumericPagination({ currentPage, totalPages, onPageChange, maxVisible = 7, className, }) {
    const [announcement, setAnnouncement] = useState('');
    const listRef = useRef(null);
    // Announce page change to AT
    useEffect(() => {
        if (currentPage > 0 && totalPages > 0) {
            setAnnouncement(`Page ${currentPage} of ${totalPages}`);
        }
    }, [currentPage, totalPages]);
    function go(page) {
        if (page < 1 || page > totalPages || page === currentPage)
            return;
        onPageChange(page);
    }
    function handleKeyDown(e) {
        if (e.key === 'Home') {
            e.preventDefault();
            go(1);
        }
        else if (e.key === 'End') {
            e.preventDefault();
            go(totalPages);
        }
        else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            go(currentPage - 1);
        }
        else if (e.key === 'ArrowRight') {
            e.preventDefault();
            go(currentPage + 1);
        }
    }
    const pages = buildPages(currentPage, totalPages, maxVisible);
    const cls = [styles.root, className].filter(Boolean).join(' ');
    return (_jsxs("nav", { "aria-label": "Pagination", className: cls, children: [_jsx("span", { role: "status", "aria-live": "polite", "aria-atomic": "true", className: styles.announce, children: announcement }), _jsxs("ol", { ref: listRef, className: styles.list, onKeyDown: handleKeyDown, children: [_jsx("li", { children: _jsx("button", { type: "button", className: styles.nav, onClick: () => go(currentPage - 1), disabled: currentPage <= 1, "aria-label": "Previous page", children: _jsx(ChevronLeft, { size: 16, "aria-hidden": "true", className: styles.icon }) }) }), pages.map((page, idx) => page === -1 ? (_jsx("li", { "aria-hidden": "true", children: _jsx("span", { className: styles.ellipsis, children: "\u2026" }) }, `ellipsis-${idx}`)) : (_jsx("li", { children: _jsx("button", { type: "button", className: [
                                styles.page,
                                page === currentPage ? styles.current : undefined,
                            ]
                                .filter(Boolean)
                                .join(' '), onClick: () => go(page), "aria-label": page === currentPage
                                ? `Page ${page}, current`
                                : `Page ${page}`, "aria-current": page === currentPage ? 'page' : undefined, "aria-disabled": page === currentPage ? 'true' : undefined, disabled: page === currentPage, children: page }) }, page))), _jsx("li", { children: _jsx("button", { type: "button", className: styles.nav, onClick: () => go(currentPage + 1), disabled: currentPage >= totalPages, "aria-label": "Next page", children: _jsx(ChevronRight, { size: 16, "aria-hidden": "true", className: styles.icon, "data-icon-mirror": "true" }) }) })] })] }));
}
//# sourceMappingURL=NumericPagination.js.map